import OneSDev from "@assets/console-images/one-s-dev-kit.png";
import OneXDev from "@assets/console-images/one-x-dev-kit.png";
import SeriesSRetail from "@assets/console-images/series-s-retail-kit.png";
import SeriesXDev from "@assets/console-images/series-x-dev-kit.png";
import SeriesXRetail from "@assets/console-images/series-x-retail-kit.png";
import OneXRetail from "@assets/console-images/one-x-retail-kit.png";
import Windows from "@assets/console-images/windows.png";
import { Device } from "@xboxstudios/hermes-apis/xboxstudios/hermes/device/v1/device_registry_pb";
import { DeviceInfo } from "@xboxstudios/hermes-apis/xboxstudios/hermes/device/v1/device_info_pb";
import { FaLaptop, FaWindows, FaXbox } from "react-icons/fa";
import { HermesAnnotationKeys } from "@components/annotations/annotationKeys";

export const DeviceImages = {
  SeriesXDev,
  SeriesSRetail,
  OneXRetail,
  OneXDev,
  OneSDev,
  Windows,
};

const trimLowerName = (name?: string) => {
  return name ? name.toLowerCase().replace(/\s/gi, "") : "";
};

// TODO: verify os names from hermes API and adjust accordingly
/**
 * Gets a generic image of the device
 * @param {Device} device
 * @returns The image URL for that device
 */
export const getDeviceImage = (device: Device) => {
  const deviceInfo = device.toObject();
  const { osFamily, deviceModel } = deviceInfo.info as DeviceInfo.AsObject;

  if (osFamily.match(/win/gi)) {
    return Windows;
  }

  if (osFamily.match(/xbox/gi)) {
    // Possible values are listed in source code for Hermes Client
    // See https://dev.azure.com/xboxstudios/Hermes/_git/hermes-client
    if (deviceModel.match(/(scarlett|Dante)/gi)) {
      return SeriesXDev;
    } else if (deviceModel.match(/seriesx|anaconda/gi)) {
      return SeriesXRetail;
    } else if (deviceModel.match(/seriess|lockhart/gi)) {
      return SeriesSRetail;
    } else if (deviceModel.match(/onexdev/gi)) {
      return OneXDev;
    } else if (deviceModel.match(/onex|xboxone/gi)) {
      return OneXRetail;
    }
  }

  return "";
};

const defaultIcon = {
  icon: FaLaptop,
  color: "",
};

/**
 * Gets the Icon for the Platform (osFamily) of the device.
 * If platform doesn't match known platforms, returs a computer icon.
 * @param {Device} device
 * @returns The icon for that device
 */
export const getDeviceIcon = (device: Device) => {
  const osFamily = device.getInfo()?.getOsFamily();

  if (!osFamily) {
    return defaultIcon;
  }

  if (osFamily.match(/xbox/gi)) {
    return { icon: FaXbox, color: "green.500" };
  } else if (osFamily.match(/win/gi)) {
    return { icon: FaWindows, color: "blue.500" };
  }

  return defaultIcon;
};

// TODO: This may not be necessary...
/**
 * Gets model info for the device
 * @param {Device} device
 * @returns The device model
 */
export const getDeviceModel = (device: Device) => {
  const trimmedDeviceOs = trimLowerName(device.getInfo()?.getOsFamily());
  if (["windows", "win"].includes(trimmedDeviceOs)) {
    return "Windows PC";
  }
  if (device.getInfo()?.getDeviceModel()) {
    return device.getInfo()?.getDeviceModel();
  }

  return "";
};

/**
 * Gets a user friendly ID for devices that have custom display names
 * @param {Device} device
 * @returns
 */
export const getUserFriendlyDeviceId = (device: Device) => {
  const info = device.toObject().info;
  const displayName = device.getDisplayName();

  if (
    (info?.osFamily === "windows" || info?.osFamily === "win") &&
    info.hostname !== displayName
  ) {
    return info.hostname;
  }

  return "";
};

/**
 * Determines if a device is running Xbox OS
 * @param {Device} device
 * @returns True if the device is an Xbox console
 */
export const isXboxDevice = (device: Device) => {
  const osFamily = device.getInfo()?.getOsFamily() || "";
  return osFamily.match(/xbox/gi) !== null;
};

/**
 * Determines if a device is running Windows
 * @param {Device} device
 * @returns True if the device is a Windows PC
 */
export const isWindowsDevice = (device: Device) => {
  const osFamily = device.getInfo()?.getOsFamily() || "";
  return osFamily.match(/win/gi) !== null;
};

const keys = Object.keys(Device.State);
/**
 * Returns the string version of the device's state enum
 * @param {Device} device
 * @returns
 */
export const getDeviceStateDisplayName = (device: Device) => {
  return keys[device.getState()];
};

/**
 * Returns the latest date the device successfully issued a refresh token.
 * @param device
 * @returns
 */
export const getDeviceLastVerified = (device: Device): Date | undefined => {
  const annotations = device.getAnnotationsMap();
  const lastSeenAnnotation = annotations.get(HermesAnnotationKeys.lastVerified);
  if (lastSeenAnnotation && lastSeenAnnotation.length > 0) {
    return new Date(lastSeenAnnotation);
  }
};

/**
 * Returns an estimate if the device's registration is likely to be expired.
 * i.e. if the device hasn't had a refresh token issued for more than 60 days.
 * @param {Device} device
 */
export const maybeDeviceRegistrationExpired = (device: Device) => {
  const lastSeen = getDeviceLastVerified(device);
  if (!lastSeen) {
    return false;
  }

  const now = new Date();
  const diff = now.getTime() - lastSeen.getTime();

  // Note: This is a rough estimate based on the device's refresh token expire time
  // which is currently 60 days. This may need to be adjusted if the refresh token expiry
  // window changes.
  const diffDays = diff / (1000 * 3600 * 24);

  return diffDays > 60;
};
